import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import css from './pages.module.css';
import commonCss from '../components/common/common.module.css';
import {useNotes} from '../context/NotesProvider';
import {useAuth} from '../context/AuthProvider';
import {useConfirm} from '../context/ConfirmProvider';
import {useCreate} from '../context/CreateProvider';
import {deleteNoteDoc} from '../services/firestoreNotes';
import type {Note} from '../types/Note';
import {Icon} from '../components/common/Icon';
import {IconButton, Spinner, Button} from '../components/common/ui';
import {formatDate, formatDuration} from '../utils';
import owlHero from '../assets/owl_hero.png';

function StatusBadge({note}: {note: Note}) {
  const {t} = useTranslation();
  if (note.status === 'error') {
    return <span className={css.statusBadgeError}>{t('Error')}</span>;
  }
  if (note.status !== 'ready') {
    return (
      <span className={css.statusBadge}>
        <Spinner />
        {t('InProgress')}
      </span>
    );
  }
  return null;
}

export function NotesPage() {
  const {t, i18n} = useTranslation();
  const {notes, loading} = useNotes();
  const {uid} = useAuth();
  const confirm = useConfirm();
  const {openRecord} = useCreate();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return notes;
    }
    return notes.filter(
      note =>
        (note.title ?? '').toLowerCase().includes(q) ||
        (note.content ?? '').toLowerCase().includes(q) ||
        (note.summarize ?? '').toLowerCase().includes(q),
    );
  }, [notes, search]);

  const removeNote = async (note: Note) => {
    if (!uid) {
      return;
    }
    const ok = await confirm({
      title: t('Delete_Note'),
      message: t('Confirm_delete_note'),
      confirmText: t('Delete'),
      danger: true,
    });
    if (ok) {
      await deleteNoteDoc(uid, note.id);
    }
  };

  return (
    <div className={css.page}>
      <header className={css.pageHeader}>
        <h1 className={css.pageTitle}>{t('Notes')}</h1>
        <div className={css.searchWrap}>
          <Icon name="search" size={16} />
          <input
            id="notes-search"
            className={css.searchInput}
            placeholder={t('Search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </header>
      <div className={css.scroll}>
        {loading && notes.length === 0 ? (
          <div className={commonCss.emptyState}>
            <Spinner large />
          </div>
        ) : filtered.length === 0 ? (
          <div className={commonCss.emptyState}>
            <div className={css.heroGlow}>
              <img src={owlHero} alt="" />
            </div>
            {notes.length === 0 ? (
              <>
                <h2 className={css.heroTitle}>{t('LetsCreateFirstNote')}</h2>
                <p style={{margin: 0}}>{t('TapNewNoteHint')}</p>
                <Button variant="primary" icon="mic" onClick={openRecord}>
                  {t('Record Audio')}
                </Button>
              </>
            ) : (
              <p>{t('Note_not_found')}</p>
            )}
          </div>
        ) : (
          <div className={css.noteList}>
            {filtered.map((note, index) => (
              <div
                key={note.id}
                className={css.noteCard}
                style={{animationDelay: `${Math.min(index, 10) * 40}ms`}}
                onClick={() => navigate(`/notes/${note.id}`)}
              >
                <div className={css.noteCardTop}>
                  <span className={css.noteEmoji}>{note.emoji ?? '📝'}</span>
                  <span className={css.noteTitle}>
                    {note.title ?? t('Note_title')}
                  </span>
                </div>
                {note.summarize || note.content ? (
                  <div className={css.notePreview}>
                    {(note.summarize ?? note.content ?? '')
                      .replace(/[#*_>`-]/g, '')
                      .slice(0, 180)}
                  </div>
                ) : null}
                <div className={css.noteMeta}>
                  <span>{formatDate(note.createdAt, i18n.language)}</span>
                  {note.fileType === 'audio' && note.duration > 0 && (
                    <span>
                      <Icon name="clock" size={12} />
                      {formatDuration(note.duration)}
                    </span>
                  )}
                  {note.fileType === 'pdf' && (
                    <span>
                      <Icon name="file" size={12} />
                      PDF
                    </span>
                  )}
                  {note.fileType === 'youtube' && (
                    <span>
                      <Icon name="youtube" size={12} />
                      YouTube
                    </span>
                  )}
                  <StatusBadge note={note} />
                </div>
                <div
                  className={css.cardDelete}
                  onClick={e => e.stopPropagation()}
                >
                  <IconButton
                    icon="trash"
                    size={15}
                    title={t('Delete')}
                    onClick={() => removeNote(note)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
